import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectBatchList, selectSingleBatchData, setBatchList } from '@reducers/batch';
import { loadJobs, selectJobsData } from '@reducers/details';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { OperationSearchItem, ProductionBatch, ProductionBatchStatistics } from '@interfaces/search';
import { DOCUMENT_ENTITY_TYPE, KEYS_TAGS, OP_TAG_NAMING } from '@components/imageHandling/common';
import { useFocusEffect } from '@react-navigation/native';
import { PaginatedResponse } from '@interfaces/machines';
import { getProductionBatchByProductionOperationId } from '@reducers/search';
import { ImageDocumentReference } from '@v2/components/imageHandling/types/types';

type LinkType = 'part' | 'operation' | 'productionOrder' | 'batch' | 'ncFile';

interface LinkMapping {
  scopeId: number;
  scopeName: string;
  machineSource: (batch: ProductionBatchStatistics) => any[] | any;
  jobSource: (job: OperationSearchItem) => any[] | any;
  getReferenceId: (item: any) => any;
  getReferenceName: (item: any, isMachine: boolean) => string;
  isArray: (isMachine: boolean) => boolean;
}

export const useBuildLinkListByPerspective = (selectedJobId: number | undefined) => {
  const isMachinePerspective = selectedJobId == null;

  const dispatch = useDispatch();

  const batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null> = useSelector(selectSingleBatchData);
  const jobs: ReducerEntityWithLoadingState<OperationSearchItem[]> = useSelector(selectJobsData);
  const batchList: ProductionBatch[] = useSelector(selectBatchList);

  const selectedJob = selectedJobId ? jobs.data?.find((j) => j.productionOperationId === +selectedJobId) : null;

  useFocusEffect(
    useCallback(() => {
      (async () => {
        if (!isMachinePerspective) {
          if (selectedJobId != null) {
            await dispatch(loadJobs({ jobIds: [selectedJobId] }));
            // Load batches for the selected job
            const batches: PaginatedResponse<ProductionBatch> = await dispatch(
              getProductionBatchByProductionOperationId({ id: selectedJobId, select: ['ProductionBatchId', 'BatchNumber'] })
            );
            if (batches?.searchResult) {
              dispatch(setBatchList(batches?.searchResult));
            }
          }
        }
        // In machine perspective, we don't need to load batches here
        // as they're already loaded from the machine context
      })();
    }, [isMachinePerspective, selectedJobId])
  );

  const linkMappings: Record<LinkType, LinkMapping> = {
    part: {
      scopeId: KEYS_TAGS.Part,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.Part],
      machineSource: (batch) => batch?.links || [],
      jobSource: (job) => job,
      getReferenceId: (item) => item?.partId,
      getReferenceName: (item, isMachine) => (isMachine ? item?.partName : item?.partNumber),
      isArray: (isMachine) => isMachine,
    },
    operation: {
      scopeId: KEYS_TAGS.Operation,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.ProductionOperation],
      machineSource: (batch) => batch?.links || [],
      jobSource: (job) => job,
      getReferenceId: (item) => item?.productionOperationId,
      getReferenceName: (item, isMachine) =>
        isMachine ? `${item?.productionOrderNumber}-${item?.productionOperationNumber}` : item?.productionOperationNumber,
      isArray: (isMachine) => isMachine,
    },
    productionOrder: {
      scopeId: KEYS_TAGS.ProductionOrder,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.ProductionOrder],
      machineSource: (batch) => batch?.links || [],
      jobSource: (job) => job,
      getReferenceId: (item) => item?.productionOrderId,
      getReferenceName: (item) => item?.productionOrderNumber,
      isArray: (isMachine) => isMachine,
    },
    batch: {
      scopeId: KEYS_TAGS.ProductionBatch,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.Batch],
      machineSource: (batch) => batch,
      jobSource: (_) => batchList || [],
      getReferenceId: (item) => item?.productionBatchId,
      getReferenceName: (item) => item?.batchNumber,
      isArray: (isMachine) => !isMachine,
    },
    ncFile: {
      scopeId: KEYS_TAGS.NcFile,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.NcFile],
      machineSource: (batch) => batch,
      jobSource: (job) => job?.linkedNcFiles || [],
      getReferenceId: (item) => item?.ncFileId,
      getReferenceName: (item) => item?.ncFileName,
      isArray: (isMachine) => !isMachine,
    },
  };

  const createLinkData = (linkType: LinkType): ImageDocumentReference[] => {
    const mapping = linkMappings[linkType];
    const sourceData = isMachinePerspective ? mapping.machineSource(batch?.data!) : mapping.jobSource(selectedJob!);

    if (!sourceData) return [];

    const items = mapping.isArray(isMachinePerspective) && Array.isArray(sourceData) ? sourceData : [sourceData];

    const seen = new Set<string>();
    const uniqueLinks: ImageDocumentReference[] = [];

    for (const item of items) {
      if (!item) continue;

      const link: ImageDocumentReference = {
        scopeId: mapping.scopeId,
        scopeName: mapping.scopeName,
        scopeReferenceId: mapping.getReferenceId(item),
        scopeReferenceName: mapping.getReferenceName(item, isMachinePerspective),
      };

      const uniqueKey = `${link.scopeId}-${link.scopeReferenceId}`;

      if (!seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniqueLinks.push(link);
      }
    }

    return [...uniqueLinks];
  };

  return useMemo(() => {
    return [
      ...createLinkData('part'),
      ...createLinkData('productionOrder'),
      ...createLinkData('operation'),
      ...createLinkData('batch'),
      ...createLinkData('ncFile'),
    ];
  }, [selectedJobId, batch?.data, selectedJob]);
};
